import { useTheme } from '@mui/material/styles';
import { Helmet } from 'react-helmet-async';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import { AppConfig } from '../../../core';

export default function CookiesPolicy() {
  const { palette, typography } = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        pt: '64px',
      }}
    >
      <Helmet>
        <title>Cookie Policy | {AppConfig.appName}</title>
        <meta name="description" content="Cookie policy for LegacyTies" />
      </Helmet>

      <Box
        sx={{
          width: '100%',
          height: 300,
          backgroundColor: palette.primary.main,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Typography
            sx={{
              color: palette.primary.contrastText,
              lineHeight: 1.25,
              fontSize: {
                xs: typography.body1.fontSize,
                md: typography.h2.fontSize,
              },
              fontWeight: { xs: 'none', md: typography.h1.fontWeight },
            }}
          >
            Cookie Policy
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: palette.primary.contrastText }}
          >
            Last updated: 17 August 2026
          </Typography>
        </Container>
      </Box>

      <Box sx={{ width: '100%', py: 4, bgcolor: palette.background.paper }}>
        <Container maxWidth="lg">
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
            <strong>1. Introduction</strong>
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 3 }}>
            This Cookie Policy explains how {AppConfig.appName} uses cookies and
            similar technologies when you visit or use our platform. Cookies
            help us support secure logins, maintain session continuity, and
            improve the overall experience for users of our family tree and
            cultural heritage tools.
          </Typography>

          <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
            <strong>2. What Are Cookies?</strong>
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 3 }}>
            Cookies are small text files stored on your device when you visit a
            website or app. They may be used to remember your preferences,
            recognise your device, and help the service function correctly.
          </Typography>

          <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
            <strong>3. Types of Cookies We Use</strong>
          </Typography>

          <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 700 }}>
            <strong>3.1 Essential Cookies</strong>
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            These cookies are necessary for the service to work correctly.
          </Typography>
          <List sx={{ listStyleType: 'disc', pl: 3, mt: 0, mb: 3 }}>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Keeping you signed in while you use the platform.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Supporting secure access and session management.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Protecting the service from unauthorised access or misuse.
            </ListItem>
          </List>

          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            <strong>3.2 Functional Cookies</strong>
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            These cookies improve usability by remembering preferences such as:
          </Typography>
          <List sx={{ listStyleType: 'disc', pl: 3, mt: 0, mb: 3 }}>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Language or region preferences.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Display or interface settings used across sessions.
            </ListItem>
          </List>

          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            <strong>3.3 Analytics and Performance Cookies</strong>
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            These cookies help us understand how users interact with the
            platform so we can improve reliability and user experience.
          </Typography>
          <List sx={{ listStyleType: 'disc', pl: 3, mt: 0, mb: 3 }}>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Measuring how pages and features are used.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Identifying technical issues or performance problems.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Supporting ongoing improvements to the service.
            </ListItem>
          </List>

          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            <strong>3.4 Third-Party Cookies</strong>
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 3 }}>
            We may use trusted third-party services to support analytics,
            hosting, or technical infrastructure. These providers may set
            cookies where needed to provide their services, subject to their own
            privacy and cookie arrangements.
          </Typography>

          <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
            <strong>4. How to Manage Cookies</strong>
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            You can usually control cookies through your browser settings.
          </Typography>
          <List sx={{ listStyleType: 'disc', pl: 3, mt: 0, mb: 3 }}>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Block or delete cookies in your browser settings.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Disable non-essential cookies if you prefer not to allow them.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Remove stored cookies from your device at any time.
            </ListItem>
          </List>
          <Typography variant="subtitle1" sx={{ mb: 3 }}>
            Please note that disabling essential cookies may affect the
            functionality of the App, including access to secure areas and
            account features.
          </Typography>

          <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
            <strong>5. Your Privacy Rights</strong>
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 3 }}>
            For information about how we handle personal data, please refer to
            our Privacy Policy. If you have questions about data protection or
            how cookies are used, you can contact us at{' '}
            {AppConfig.appContactEmail}.
          </Typography>

          <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
            <strong>6. Changes to This Cookie Policy</strong>
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 3 }}>
            We may update this Cookie Policy from time to time to reflect
            changes in our practices, technology, or legal requirements. Any
            major updates will be communicated through the platform where
            appropriate.
          </Typography>

          <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
            <strong>7. Contact</strong>
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            If you have questions about this Cookie Policy, please contact us at{' '}
            {AppConfig.appContactEmail}.
          </Typography>
          <Typography variant="subtitle1">
            Website: https://legacy-ties.com
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}
